import { useEffect, useState, useCallback } from "react";

function DentalPositionInput({
    multiple,
    project, setProject,
    index,
    disabledTeeth,
    disabled
}) {
    const [selected, setSelected] = useState([]);
    const [hovered, setHovered] = useState([]);
    const [isReady, setIsReady] = useState(false);

    const callUpdateProjectProperty = useCallback(
        (value, index) => {
            let items = project.items;
            items[index].teeth = [];
            value.forEach(x => {
                items[index].teeth.push({
                    'position': x,
                    'type': 'polished'
                });
            });

            setProject(prevProject => ({
                ...prevProject,
                'items': items
            }));

            setIsReady(false);
        },
        [project, setProject]
    );

    useEffect(() => {
        if (isReady)
            callUpdateProjectProperty(selected, index);
    }, [isReady, index, selected, callUpdateProjectProperty]);

    useEffect(() => {
        setSelected(project.items[index].teeth.map(x => x.position));
    }, [project.items, index]);
    
    const teethPositions = [];

    for (let i = 1; i <= 4; i++) {
        teethPositions[i - 1] = [];
        for (let j = 1; j <= 8; j++) {
            teethPositions[i - 1][(i === 2 || i === 4) ? j - 1 : 8 - j] = `${i}${j}`;
        }
    }
    
    function selectTooth(position) {
        if (!disabledTeeth.includes(position)) {
            setIsReady(false);
            callUpdateProjectProperty([], index);
            if (multiple) {
                if (selected.length === 0) {
                    setSelected([position]);
                    setHovered([position]);
                }
                else if (selected.length === 1 && position !== selected[0]) {
                    selectAsRange(position);
                    setIsReady(true);
                } else
                    setSelected([position]);
            } else {
                setSelected(!selected.includes(position) ? [position] : []);
                setIsReady(true);
            }
        }
    }

    function selectAsRange(to) {
        const from = selected[0];
        if (from <= 28) {
            if (to <= 28) {
                addRange(from, to, [...teethPositions[0], ...teethPositions[1]]);
                setHovered([]);
            } else
                setSelected([to]);
        } else {
            if (to >= 31 && to <= 48) {
                addRange(from, to, [...teethPositions[2], ...teethPositions[3]]);
                setHovered([]);
            } else
                setSelected([to]);
        }
    }

    function addRange(from, to, mergedPositions) {
        let newSelected = [];
        for (let i = mergedPositions.indexOf(mergedPositions.indexOf(from) < mergedPositions.indexOf(to) ? from : to); i <= mergedPositions.indexOf(mergedPositions.indexOf(from) < mergedPositions.indexOf(to) ? to : from); i++) {
            if (!newSelected.includes(mergedPositions[i].toString())) {
                newSelected.push(mergedPositions[i].toString());
            }

            if (disabledTeeth.includes(mergedPositions[i].toString())) {
                newSelected = [from];
                break;
            }
        }

        setSelected(newSelected);
    }

    function hoverTooth(to) {
        // from selected to tooth
        if (multiple && selected.length === 1) {
            const from = selected[0];
            if (from <= 28) {
                if (to <= 28)
                    hoverRange(from, to, [...teethPositions[0], ...teethPositions[1]]);
                else
                    setHovered([]);
            } else {
                if (to >= 31 && to <= 48)
                    hoverRange(from, to, [...teethPositions[2], ...teethPositions[3]]);
                else
                    setHovered([]);
            }
        }
    }

    function hoverRange(from, to, mergedPositions) {
        let newHovered = [];
        for (let i = mergedPositions.indexOf(mergedPositions.indexOf(from) < mergedPositions.indexOf(to) ? from : to); i <= mergedPositions.indexOf(mergedPositions.indexOf(from) < mergedPositions.indexOf(to) ? to : from); i++) {
            if (!newHovered.includes(mergedPositions[i].toString())) {
                newHovered.push(mergedPositions[i].toString());
            }

            if (disabledTeeth.includes(mergedPositions[i].toString())) {
                newHovered = [from];
                break;
            }
        }

        const fromIndex = newHovered.indexOf(from);
        fromIndex === 0 ? newHovered.shift() : newHovered.splice(fromIndex);
        setHovered(newHovered);
    }

    function getToothElementClass(tooth) {
        if (!disabled && !selected.includes(tooth) && !hovered.includes(tooth) && !disabledTeeth.includes(tooth))
            return 'tooth-button';
        if (hovered.includes(tooth))
            return 'hovered-tooth-button';
        if (selected.includes(tooth))
            return 'selected-tooth-button';
        if (disabledTeeth.includes(tooth))
            return 'disabled-tooth';
    }
    
    return (
        <div className="DentalPositionInput">
            <div className='row border-bottom'>
                <div className="col-sm-6 d-flex">
                    {teethPositions[0].map(tooth => (
                        <div className="col text-center" key={tooth}>
                            <div className={`py-2 ${getToothElementClass(tooth)}`} onClick={() => {if (!disabled) selectTooth(tooth)}} onMouseOver={() => {if (!disabled) hoverTooth(tooth)}}>{tooth}</div>
                        </div>
                    ))}
                </div>
                <div className="col-sm-6 d-flex border-start">
                    {teethPositions[1].map(tooth => (
                        <div className="col text-center" key={tooth}>
                            <div className={`py-2 ${getToothElementClass(tooth)}`} onClick={() => {if (!disabled) selectTooth(tooth)}} onMouseOver={() => {if (!disabled) hoverTooth(tooth)}}>{tooth}</div>
                        </div>
                    ))}
                </div>
            </div>
            <div className='row'>
            <div className="col-sm-6 d-flex">
                    {teethPositions[2].map(tooth => (
                        <div className="col text-center" key={tooth}>
                            <div className={`py-2 ${getToothElementClass(tooth)}`} onClick={() => {if (!disabled) selectTooth(tooth)}} onMouseOver={() => {if (!disabled) hoverTooth(tooth)}}>{tooth}</div>
                        </div>
                    ))}
                </div>
                <div className="col-sm-6 d-flex border-start">
                    {teethPositions[3].map(tooth => (
                        <div className="col text-center" key={tooth}>
                            <div className={`py-2 ${getToothElementClass(tooth)}`} onClick={() => {if (!disabled) selectTooth(tooth)}} onMouseOver={() => {if (!disabled) hoverTooth(tooth)}}>{tooth}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default DentalPositionInput;